//
// Copyright 2026 DXOS.org
//

import { tv } from '@dxos/ui-theme';

/**
 * Tailwind-variants theme for all react-ui-list components.
 * Consumers call `listTheme.styles()` to get the slot functions.
 * Each slot function accepts an optional `{ class: ... }` override for per-instance merging.
 *
 * Structural pattern shared across list components:
 * - viewport: ScrollArea wrapper (`dx-expand`)
 * - content:  flex-col item container
 * - item:     base row (interactive affordances come from dx-hover / dx-selected / dx-current)
 */
const listStyles = tv({
  slots: {
    //
    // Listbox
    //
    listboxViewport: 'dx-expand',
    listboxContent: 'flex flex-col',
    // `dx-selected` pairs with `aria-selected="true"` set per-option (see
    // `ui-theme/src/css/components/state.md`). `outline-none` removes the native focus
    // ring; `dx-focus-ring-inset` restores a themed indicator on the focused row — without it,
    // arrow navigation is invisible wherever selection does not follow focus (multi-select).
    listboxItem:
      'flex items-center dx-hover dx-selected min-h-(--dx-control) px-(--dx-control-pad) cursor-pointer outline-none dx-focus-ring-inset',
    listboxItemLabel: 'grow truncate',

    //
    // OrderedList
    //
    orderedListViewport: 'dx-expand',
    orderedListContent: 'flex flex-col',
    // `dx-current` enables `aria-current` row styling (not listbox/option semantics).
    orderedListItem: 'relative dx-current',
    // Bordered column wrapping title + detail panel in the master-detail layout.
    orderedListDetailColumn: 'flex flex-col ring-1 ring-separator-subtle rounded-sm overflow-hidden',
    // `min-h` matches the shared rail-item track so handles, title, and caret share a baseline.
    orderedListDetailTitleRow: 'flex items-center min-h-[var(--dx-rail-item)]',
    orderedListDetailPanel: 'px-trim-sm pb-trim-sm',
    orderedListTitle: 'flex grow items-center truncate cursor-pointer',

    //
    // Picker
    //
    // `px-[var(--gutter,…)]` aligns padding with sibling `Column.Center` content, falling back to
    // the density control pad when not nested under `Column.Root`.
    pickerItem:
      'dx-hover dx-selected flex items-center min-h-(--dx-control) px-[var(--gutter,var(--dx-control-pad))] cursor-pointer select-none',

    //
    // Combobox
    //
    // `m-form-chrome mb-0` mirrors the rest of the form-chrome padding convention.
    comboboxInput: 'm-form-chrome mb-0 w-[calc(100%-2*var(--spacing-form-chrome))]',
    comboboxList: 'p-form-chrome',
    // Trigger value / placeholder text — grows and truncates; subdued when placeholder.
    comboboxTriggerText: 'font-normal text-start flex-1 min-w-0 truncate me-trim-sm',
    // Item row adds flex layout; `dx-hover`/`dx-selected` and padding come from `Picker.Item`.
    comboboxItem: 'flex w-full gap-trim-sm items-center',
    comboboxItemDescription: 'text-sm text-fg-muted truncate',

    //
    // ItemContent
    //
    // Grid whose columns/placement come from the `hasIcon` variant: a leading rail-item icon track
    // only when an icon is present, so an icon-less row doesn't reserve (and indent past) empty space.
    itemContentRoot: 'grid items-center gap-x-trim-sm w-full min-w-0',
    // Flex so an inline icon (avatar span, svg) is blockified and gets no line-box descender gap below it.
    itemContentIcon: 'col-start-1 row-start-1 place-self-center flex',
    itemContentTitle: 'row-start-1 min-w-0 truncate',
    itemContentDescription: 'row-start-2 min-w-0 truncate text-sm text-fg-muted',

    //
    // Empty
    //
    empty: 'flex flex-col items-center justify-center gap-trim-sm p-trim-lg text-sm text-center text-fg-muted',
  },
  variants: {
    // Reserve the leading icon track only when an icon is rendered; otherwise the content occupies a
    // single full-width column instead of being indented past an empty icon slot.
    hasIcon: {
      true: {
        // Grows past the rail width for wide icons (avatars) rather than letting them overflow.
        itemContentRoot: 'grid-cols-[minmax(var(--dx-rail-item),auto)_minmax(0,1fr)]',
        itemContentTitle: 'col-start-2',
        itemContentDescription: 'col-start-2',
      },
      false: {
        itemContentRoot: 'grid-cols-[minmax(0,1fr)]',
        itemContentTitle: 'col-start-1',
        itemContentDescription: 'col-start-1',
      },
    },
  },
  defaultVariants: {
    hasIcon: true,
  },
});

/** react-ui-list theme: call `.styles()` to get per-slot class functions. */
export const listTheme = {
  styles: listStyles,
};

/** Slot names of {@link listTheme.styles}, for `bridgeTv` registration. */
export const listSlots = Object.keys(listTheme.styles()) as Array<keyof ReturnType<typeof listTheme.styles>>;
